import React, { useState, useEffect, useMemo } from 'react';
import { useMsal } from '@azure/msal-react';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Stack,
  Chip,
  Paper,
  Grid,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Switch,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  IconButton,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import StorageIcon from '@mui/icons-material/Storage';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import KeyIcon from '@mui/icons-material/Key';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

interface MsalCacheInspectorProps {
  onBack?: () => void;
}

interface CacheEntry {
  key: string;
  value: any;
  type: 'Account' | 'AccessToken' | 'RefreshToken' | 'IdToken' | 'Other';
  storageType: 'sessionStorage' | 'localStorage' | 'memory';
  parsedKey?: {
    clientId?: string;
    environment?: string;
    homeAccountId?: string;
    realm?: string;
    credentialType?: string;
    target?: string;
  };
  expiresOn?: Date;
  cachedAt?: Date;
}

interface TokenSimulationRequest {
  scopes: string[];
  account: string;
}

const MsalCacheInspector: React.FC<MsalCacheInspectorProps> = ({ onBack }) => {
  const { instance, accounts } = useMsal();
  const [cacheEntries, setCacheEntries] = useState<CacheEntry[]>([]);
  const [showPII, setShowPII] = useState(false);
  const [showExpired, setShowExpired] = useState(true);
  const [copyOk, setCopyOk] = useState(false);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const [expandedEntry, setExpandedEntry] = useState<string | null>(null);
  
  // Simulation state
  const [simScopes, setSimScopes] = useState('User.Read');
  const [simResult, setSimResult] = useState<{
    found: boolean;
    reason: string;
    matchedEntry?: CacheEntry;
  } | null>(null);

  // Parse MSAL cache key format
  const parseCacheKey = (key: string): CacheEntry['parsedKey'] => {
    const parts = key.split('-');
    const parsed: CacheEntry['parsedKey'] = {};
    
    // MSAL keys follow patterns like:
    // {clientId}-{environment}-{homeAccountId}-{realm}-{credentialType}-{target}
    if (key.includes('.') && parts.length > 0) {
      // Try to identify components
      parsed.clientId = parts[0];
      if (key.includes('login.microsoftonline.com') || key.includes('login.windows.net')) {
        const envMatch = key.match(/(login\.[a-z.]+)/);
        if (envMatch) parsed.environment = envMatch[1];
      }
      
      // Look for credential type
      if (key.includes('accesstoken')) parsed.credentialType = 'AccessToken';
      else if (key.includes('refreshtoken')) parsed.credentialType = 'RefreshToken';
      else if (key.includes('idtoken')) parsed.credentialType = 'IdToken';
      else if (key.includes('account')) parsed.credentialType = 'Account';
      
      // Try to extract scopes/target (usually after last hyphen cluster)
      const targetMatch = key.match(/-([\w\s.:/]+)$/);
      if (targetMatch) parsed.target = targetMatch[1];
    }
    
    return parsed;
  };

  // Detect entry type from key and value
  const detectEntryType = (key: string, value: any): CacheEntry['type'] => {
    const lowerKey = key.toLowerCase();
    if (lowerKey.includes('accesstoken') || value?.credentialType === 'AccessToken') return 'AccessToken';
    if (lowerKey.includes('refreshtoken') || value?.credentialType === 'RefreshToken') return 'RefreshToken';
    if (lowerKey.includes('idtoken') || value?.credentialType === 'IdToken') return 'IdToken';
    if (lowerKey.includes('account') || value?.homeAccountId) return 'Account';
    return 'Other';
  };

  // Load cache entries from browser storage
  const loadCacheEntries = () => {
    const entries: CacheEntry[] = [];
    
    // Check both sessionStorage and localStorage
    const storages: Array<{ storage: Storage; type: 'sessionStorage' | 'localStorage' }> = [
      { storage: sessionStorage, type: 'sessionStorage' },
      { storage: localStorage, type: 'localStorage' },
    ];
    
    storages.forEach(({ storage, type }) => {
      for (let i = 0; i < storage.length; i++) {
        const key = storage.key(i);
        if (!key) continue;
        
        // Only process MSAL-related keys
        if (!key.includes('msal') && !key.includes('login.microsoftonline.com') && !key.includes('login.windows.net')) {
          continue;
        }
        
        try {
          const rawValue = storage.getItem(key);
          if (!rawValue) continue;
          
          const value = JSON.parse(rawValue);
          const entryType = detectEntryType(key, value);
          
          const entry: CacheEntry = {
            key,
            value,
            type: entryType,
            storageType: type,
            parsedKey: parseCacheKey(key),
          };
          
          // Extract expiration if available
          if (value.expiresOn) {
            entry.expiresOn = new Date(parseInt(value.expiresOn) * 1000);
          }
          if (value.cachedAt) {
            entry.cachedAt = new Date(parseInt(value.cachedAt) * 1000);
          }
          
          entries.push(entry);
        } catch (err) {
          // Skip entries that can't be parsed
          console.warn('Failed to parse cache entry:', key, err);
        }
      }
    });
    
    setCacheEntries(entries);
    setLastRefresh(new Date());
  };

  // Auto-load on mount
  useEffect(() => {
    loadCacheEntries();
  }, []);

  // Filter entries
  const filteredEntries = useMemo(() => {
    if (showExpired) return cacheEntries;
    return cacheEntries.filter(entry => {
      if (!entry.expiresOn) return true; // Show non-expiring entries
      return entry.expiresOn > new Date(); // Only show non-expired
    });
  }, [cacheEntries, showExpired]);

  // Group entries by type
  const groupedEntries = useMemo(() => {
    const groups: Record<CacheEntry['type'], CacheEntry[]> = {
      Account: [],
      AccessToken: [],
      RefreshToken: [],
      IdToken: [],
      Other: [],
    };
    
    filteredEntries.forEach(entry => {
      groups[entry.type].push(entry);
    });
    
    return groups;
  }, [filteredEntries]);

  // Simulate acquireTokenSilent
  const simulateTokenAcquisition = () => {
    const requestedScopes = simScopes.split(/[,\s]+/).filter(s => s.trim());
    
    if (requestedScopes.length === 0) {
      setSimResult({
        found: false,
        reason: 'No scopes provided for simulation',
      });
      return;
    }
    
    const activeAccount = instance.getActiveAccount() || accounts[0];
    if (!activeAccount) {
      setSimResult({
        found: false,
        reason: 'No active account - user must sign in first',
      });
      return;
    }
    
    // Look for matching access token in cache
    const matchingToken = groupedEntries.AccessToken.find(entry => {
      // Check if token is for the active account
      const accountMatch = entry.value?.homeAccountId === activeAccount.homeAccountId;
      if (!accountMatch) return false;
      
      // Check if token has expired
      if (entry.expiresOn && entry.expiresOn < new Date()) return false;
      
      // Check if scopes match (simplified - real MSAL does more complex matching)
      const tokenScopes = (entry.value?.target || '').toLowerCase().split(' ');
      const requestMatch = requestedScopes.every((reqScope: string) => 
        tokenScopes.some((tokenScope: string) => tokenScope.includes(reqScope.toLowerCase()))
      );
      
      return requestMatch;
    });
    
    if (matchingToken) {
      setSimResult({
        found: true,
        reason: `✅ Cache hit! Valid access token found with matching scopes.`,
        matchedEntry: matchingToken,
      });
    } else {
      // Check if refresh token exists
      const refreshToken = groupedEntries.RefreshToken.find(entry => 
        entry.value?.homeAccountId === activeAccount.homeAccountId
      );
      
      if (refreshToken) {
        setSimResult({
          found: false,
          reason: '⚠️ No cached access token, but refresh token available. MSAL would attempt silent refresh.',
        });
      } else {
        setSimResult({
          found: false,
          reason: '❌ No cached token and no refresh token. InteractionRequiredAuthError would be thrown.',
        });
      }
    }
  };

  // Redact PII from value
  const redactValue = (value: any, type: CacheEntry['type']): any => {
    if (showPII) return value;
    
    const redacted = { ...value };
    
    // Redact sensitive fields
    const sensitiveFields = [
      'secret', 'accessToken', 'idToken', 'refreshToken',
      'username', 'name', 'email', 'preferred_username',
      'given_name', 'family_name', 'unique_name'
    ];
    
    Object.keys(redacted).forEach(key => {
      if (sensitiveFields.some(field => key.toLowerCase().includes(field))) {
        redacted[key] = '[REDACTED]';
      }
    });
    
    return redacted;
  };

  // Copy to clipboard
  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopyOk(true);
      setTimeout(() => setCopyOk(false), 1500);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Clear cache
  const clearCache = () => {
    if (!window.confirm('Clear all MSAL cache entries? This will sign you out.')) return;
    
    // Clear MSAL entries from both storages
    [sessionStorage, localStorage].forEach(storage => {
      const keysToRemove: string[] = [];
      for (let i = 0; i < storage.length; i++) {
        const key = storage.key(i);
        if (key && (key.includes('msal') || key.includes('login.microsoftonline.com') || key.includes('login.windows.net'))) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => storage.removeItem(key));
    });
    
    loadCacheEntries();
  };

  // Format time remaining
  const formatTimeRemaining = (expiresOn: Date): { text: string; color: string; expired: boolean } => {
    const now = new Date();
    const diff = expiresOn.getTime() - now.getTime();
    
    if (diff < 0) {
      return { text: 'Expired', color: 'error.main', expired: true };
    }
    
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    
    if (days > 0) return { text: `${days}d ${hours % 24}h`, color: 'success.main', expired: false };
    if (hours > 0) return { text: `${hours}h ${minutes % 60}m`, color: hours > 1 ? 'success.main' : 'warning.main', expired: false };
    if (minutes > 5) return { text: `${minutes}m`, color: 'warning.main', expired: false };
    return { text: `${minutes}m`, color: 'error.main', expired: false };
  };

  // Render entry card
  const renderEntryCard = (entry: CacheEntry, index: number) => {
    const isExpanded = expandedEntry === entry.key;
    const timeInfo = entry.expiresOn ? formatTimeRemaining(entry.expiresOn) : null;
    
    return (
      <Accordion
        key={index}
        expanded={isExpanded}
        onChange={() => setExpandedEntry(isExpanded ? null : entry.key)}
        sx={{
          border: '1px solid',
          borderColor: timeInfo?.expired ? 'error.light' : 'divider',
          borderRadius: 2,
          mb: 1,
          '&:before': { display: 'none' },
          bgcolor: timeInfo?.expired ? '#fff5f5' : 'white',
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexGrow: 1, pr: 2 }}>
            {entry.type === 'AccessToken' && <VpnKeyIcon color="primary" fontSize="small" />}
            {entry.type === 'RefreshToken' && <RefreshIcon color="secondary" fontSize="small" />}
            {entry.type === 'IdToken' && <KeyIcon color="info" fontSize="small" />}
            {entry.type === 'Account' && <AccountCircleIcon color="success" fontSize="small" />}
            {entry.type === 'Other' && <StorageIcon color="action" fontSize="small" />}
            
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace', fontSize: '0.8rem' }}>
                {entry.key.length > 60 ? `${entry.key.substring(0, 57)}...` : entry.key}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                <Chip label={entry.storageType} size="small" sx={{ fontSize: '0.65rem', height: 18 }} />
                {entry.parsedKey?.target && (
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                    {entry.parsedKey.target.length > 40 
                      ? `${entry.parsedKey.target.substring(0, 37)}...` 
                      : entry.parsedKey.target}
                  </Typography>
                )}
              </Box>
            </Box>
            
            {timeInfo && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {timeInfo.expired ? (
                  <ErrorIcon sx={{ fontSize: '1rem', color: timeInfo.color }} />
                ) : (
                  <CheckCircleIcon sx={{ fontSize: '1rem', color: timeInfo.color }} />
                )}
                <Typography variant="caption" sx={{ color: timeInfo.color, fontWeight: 600 }}>
                  {timeInfo.text}
                </Typography>
              </Box>
            )}
          </Box>
        </AccordionSummary>
        
        <AccordionDetails>
          <Stack spacing={2}>
            {/* Parsed Key Info */}
            {entry.parsedKey && Object.keys(entry.parsedKey).length > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Cache Key Components:
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableBody>
                      {Object.entries(entry.parsedKey).map(([key, value]) => (
                        value && (
                          <TableRow key={key}>
                            <TableCell sx={{ fontWeight: 600, width: '30%' }}>{key}</TableCell>
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                              {value}
                            </TableCell>
                          </TableRow>
                        )
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
            
            {/* Time Info */}
            {(entry.expiresOn || entry.cachedAt) && (
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Timing Information:
                </Typography>
                <Grid container spacing={2}>
                  {entry.cachedAt && (
                    <Grid item xs={12} sm={6}>
                      <Paper variant="outlined" sx={{ p: 1.5 }}>
                        <Typography variant="caption" color="text.secondary">Cached At</Typography>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                          {entry.cachedAt.toLocaleString()}
                        </Typography>
                      </Paper>
                    </Grid>
                  )}
                  {entry.expiresOn && (
                    <Grid item xs={12} sm={6}>
                      <Paper 
                        variant="outlined" 
                        sx={{ 
                          p: 1.5,
                          borderColor: timeInfo?.expired ? 'error.light' : 'divider',
                          bgcolor: timeInfo?.expired ? '#fff5f5' : 'transparent'
                        }}
                      >
                        <Typography variant="caption" color="text.secondary">Expires On</Typography>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                          {entry.expiresOn.toLocaleString()}
                        </Typography>
                        <Typography variant="caption" sx={{ color: timeInfo?.color, fontWeight: 600 }}>
                          {timeInfo?.text}
                        </Typography>
                      </Paper>
                    </Grid>
                  )}
                </Grid>
              </Box>
            )}
            
            {/* Raw Value */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Cache Value:
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => copyToClipboard(JSON.stringify(redactValue(entry.value, entry.type), null, 2))}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Box>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  bgcolor: '#f5f5f5',
                  maxHeight: 300,
                  overflow: 'auto',
                }}
              >
                <pre style={{ margin: 0, fontSize: '0.75rem', fontFamily: 'monospace' }}>
                  {JSON.stringify(redactValue(entry.value, entry.type), null, 2)}
                </pre>
              </Paper>
            </Box>
          </Stack>
        </AccordionDetails>
      </Accordion>
    );
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>
      {/* Header Card */}
      <Card variant="outlined" sx={{ mb: 4, borderRadius: 2, overflow: 'hidden' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            p: 2,
            backgroundColor: '#f4f8fb',
            borderBottom: '1px solid rgba(0, 0, 0, 0.12)'
          }}
        >
          {onBack && (
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={onBack}
              sx={{
                mr: 2,
                borderRadius: 2,
                pl: 1.5,
                color: "text.secondary",
                borderColor: "divider",
                "&:hover": {
                  backgroundColor: "rgba(0,0,0,0.04)",
                  borderColor: "primary.main",
                  color: "primary.main"
                }
              }}
            >
              Back
            </Button>
          )}

          <Box sx={{ borderLeft: '4px solid', borderColor: 'primary.main', pl: 2, py: 0.5 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 600,
                color: 'primary.main',
                fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                letterSpacing: '-0.01em',
              }}
            >
              MSAL Cache Inspector
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
              }}
            >
              Inspect browser storage and understand MSAL's caching behavior
            </Typography>
          </Box>
        </Box>
      </Card>

      {/* Controls */}
      <Card variant="outlined" sx={{ borderRadius: 2, mb: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Grid container spacing={3} alignItems="center">
            {/* Stats */}
            <Grid item xs={12} sm={4}>
              <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', bgcolor: '#f8f9fa' }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>
                  {filteredEntries.length}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Cache Entries
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', bgcolor: '#f8f9fa' }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: 'success.main' }}>
                  {groupedEntries.AccessToken.filter(e => !e.expiresOn || e.expiresOn > new Date()).length}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Valid Access Tokens
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', bgcolor: '#f8f9fa' }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  Last Refreshed
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {lastRefresh.toLocaleTimeString()}
                </Typography>
              </Paper>
            </Grid>

            {/* Toggles and Actions */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 2, mb: 1 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={showPII}
                      onChange={(e) => setShowPII(e.target.checked)}
                      color="warning"
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {showPII ? <VisibilityIcon fontSize="small" /> : <VisibilityOffIcon fontSize="small" />}
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Show PII
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={showExpired}
                      onChange={(e) => setShowExpired(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      Show Expired
                    </Typography>
                  }
                />
                
                <Box sx={{ flexGrow: 1 }} />
                
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={loadCacheEntries}
                  sx={{ borderRadius: 2 }}
                >
                  Refresh
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteSweepIcon />}
                  onClick={clearCache}
                  sx={{ borderRadius: 2 }}
                >
                  Clear Cache
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* acquireTokenSilent Simulator */}
      <Card variant="outlined" sx={{ borderRadius: 2, mb: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
            <HelpOutlineIcon color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              acquireTokenSilent Simulator
            </Typography>
            <Tooltip title="Test if MSAL would find a cached token for given scopes">
              <InfoOutlinedIcon fontSize="small" color="action" />
            </Tooltip>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter scopes to check if MSAL would return a cached token (without making actual API calls):
          </Typography>

          <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={12} sm={8}>
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                  Scopes (space or comma separated):
                </Typography>
                <input
                  type="text"
                  value={simScopes}
                  onChange={(e) => setSimScopes(e.target.value)}
                  placeholder="User.Read Mail.Read"
                  style={{
                    width: '100%',
                    border: 'none',
                    outline: 'none',
                    fontFamily: 'monospace',
                    fontSize: '0.9rem',
                    padding: '4px 0',
                  }}
                />
              </Paper>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Button
                fullWidth
                variant="contained"
                onClick={simulateTokenAcquisition}
                disabled={!accounts.length}
                sx={{ height: 56, borderRadius: 2, fontWeight: 600 }}
              >
                Simulate
              </Button>
            </Grid>

            {simResult && (
              <Grid item xs={12}>
                <Alert
                  severity={simResult.found ? 'success' : 'warning'}
                  sx={{ borderRadius: 2 }}
                  action={
                    simResult.matchedEntry && (
                      <Button
                        size="small"
                        onClick={() => setExpandedEntry(simResult.matchedEntry!.key)}
                      >
                        View Entry
                      </Button>
                    )
                  }
                >
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Simulation Result:
                  </Typography>
                  <Typography variant="body2">
                    {simResult.reason}
                  </Typography>
                </Alert>
              </Grid>
            )}

            {!accounts.length && (
              <Grid item xs={12}>
                <Alert severity="info" sx={{ borderRadius: 2 }}>
                  Please sign in to enable simulation
                </Alert>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      {/* Cache Entries by Type */}
      {filteredEntries.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 6, textAlign: 'center', borderRadius: 2, bgcolor: '#fafafa' }}>
          <StorageIcon sx={{ fontSize: '3rem', color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 600, mb: 1 }}>
            No Cache Entries Found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Sign in to populate the MSAL cache, or enable "Show Expired" to see historical entries.
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={2}>
          {/* Access Tokens */}
          {groupedEntries.AccessToken.length > 0 && (
            <Accordion defaultExpanded sx={{ borderRadius: 2, '&:before': { display: 'none' } }}>
              <AccordionSummary 
                expandIcon={<ExpandMoreIcon />}
                sx={{ 
                  bgcolor: '#f5f5f5',
                  borderRadius: 2,
                  '&.Mui-expanded': {
                    borderBottomLeftRadius: 0,
                    borderBottomRightRadius: 0,
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <VpnKeyIcon color="primary" />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Access Tokens
                  </Typography>
                  <Chip label={groupedEntries.AccessToken.length} size="small" color="primary" />
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                    Short-lived tokens used to access protected resources (APIs)
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 2 }}>
                <Stack spacing={1.5}>
                  {groupedEntries.AccessToken.map((entry, idx) => renderEntryCard(entry, idx))}
                </Stack>
              </AccordionDetails>
            </Accordion>
          )}

          {/* Refresh Tokens */}
          {groupedEntries.RefreshToken.length > 0 && (
            <Accordion sx={{ borderRadius: 2, '&:before': { display: 'none' } }}>
              <AccordionSummary 
                expandIcon={<ExpandMoreIcon />}
                sx={{ 
                  bgcolor: '#f5f5f5',
                  borderRadius: 2,
                  '&.Mui-expanded': {
                    borderBottomLeftRadius: 0,
                    borderBottomRightRadius: 0,
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <RefreshIcon color="secondary" />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Refresh Tokens
                  </Typography>
                  <Chip label={groupedEntries.RefreshToken.length} size="small" color="secondary" />
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                    Long-lived tokens used by MSAL to silently obtain new access tokens
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 2 }}>
                <Stack spacing={1.5}>
                  {groupedEntries.RefreshToken.map((entry, idx) => renderEntryCard(entry, idx))}
                </Stack>
              </AccordionDetails>
            </Accordion>
          )}

          {/* ID Tokens */}
          {groupedEntries.IdToken.length > 0 && (
            <Accordion sx={{ borderRadius: 2, '&:before': { display: 'none' } }}>
              <AccordionSummary 
                expandIcon={<ExpandMoreIcon />}
                sx={{ 
                  bgcolor: '#f5f5f5',
                  borderRadius: 2,
                  '&.Mui-expanded': {
                    borderBottomLeftRadius: 0,
                    borderBottomRightRadius: 0,
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <KeyIcon color="info" />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    ID Tokens
                  </Typography>
                  <Chip label={groupedEntries.IdToken.length} size="small" color="info" />
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                    Tokens containing user identity claims (name, email, etc.)
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 2 }}>
                <Stack spacing={1.5}>
                  {groupedEntries.IdToken.map((entry, idx) => renderEntryCard(entry, idx))}
                </Stack>
              </AccordionDetails>
            </Accordion>
          )}

          {/* Accounts */}
          {groupedEntries.Account.length > 0 && (
            <Accordion sx={{ borderRadius: 2, '&:before': { display: 'none' } }}>
              <AccordionSummary 
                expandIcon={<ExpandMoreIcon />}
                sx={{ 
                  bgcolor: '#f5f5f5',
                  borderRadius: 2,
                  '&.Mui-expanded': {
                    borderBottomLeftRadius: 0,
                    borderBottomRightRadius: 0,
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AccountCircleIcon color="success" />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Accounts
                  </Typography>
                  <Chip label={groupedEntries.Account.length} size="small" color="success" />
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                    User account information (username, tenant, home account ID)
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 2 }}>
                <Stack spacing={1.5}>
                  {groupedEntries.Account.map((entry, idx) => renderEntryCard(entry, idx))}
                </Stack>
              </AccordionDetails>
            </Accordion>
          )}

          {/* Other */}
          {groupedEntries.Other.length > 0 && (
            <Accordion sx={{ borderRadius: 2, '&:before': { display: 'none' } }}>
              <AccordionSummary 
                expandIcon={<ExpandMoreIcon />}
                sx={{ 
                  bgcolor: '#f5f5f5',
                  borderRadius: 2,
                  '&.Mui-expanded': {
                    borderBottomLeftRadius: 0,
                    borderBottomRightRadius: 0,
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <StorageIcon color="action" />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Other MSAL Data
                  </Typography>
                  <Chip label={groupedEntries.Other.length} size="small" />
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                    Metadata and configuration entries
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 2 }}>
                <Stack spacing={1.5}>
                  {groupedEntries.Other.map((entry, idx) => renderEntryCard(entry, idx))}
                </Stack>
              </AccordionDetails>
            </Accordion>
          )}
        </Stack>
      )}

      {/* Educational Info */}
      <Card variant="outlined" sx={{ borderRadius: 2, mt: 3 }}>
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <InfoOutlinedIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Understanding MSAL Cache Keys
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={3}>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                  Cache Key Format:
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', mb: 1 }}>
                    {'{clientId}-{environment}-{homeAccountId}-{realm}-{credentialType}-{target}'}
                  </Typography>
                  <Divider sx={{ my: 1.5 }} />
                  <Stack spacing={1}>
                    <Box>
                      <Typography variant="caption" sx={{ fontWeight: 600 }}>clientId:</Typography>
                      <Typography variant="caption" color="text.secondary"> Your application's client ID (from Entra ID registration)</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ fontWeight: 600 }}>environment:</Typography>
                      <Typography variant="caption" color="text.secondary"> login.microsoftonline.com (or other cloud)</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ fontWeight: 600 }}>homeAccountId:</Typography>
                      <Typography variant="caption" color="text.secondary"> Unique identifier for the user account</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ fontWeight: 600 }}>realm:</Typography>
                      <Typography variant="caption" color="text.secondary"> Tenant ID where the user authenticated</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ fontWeight: 600 }}>credentialType:</Typography>
                      <Typography variant="caption" color="text.secondary"> AccessToken, RefreshToken, IdToken, or Account</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ fontWeight: 600 }}>target:</Typography>
                      <Typography variant="caption" color="text.secondary"> The scopes/permissions this token grants (e.g., "User.Read Mail.Read")</Typography>
                    </Box>
                  </Stack>
                </Paper>
              </Box>

              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                  How MSAL Uses the Cache:
                </Typography>
                <Stack spacing={1.5}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 24 }}>1.</Typography>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          acquireTokenSilent() checks cache
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          MSAL builds a cache key from your request (clientId + account + scopes) and looks for a matching access token
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>

                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 24 }}>2.</Typography>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Token found & valid → Return immediately
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          If cached token exists and hasn't expired, MSAL returns it without any network call
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>

                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 24 }}>3.</Typography>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Token expired → Use refresh token
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          MSAL silently uses the refresh token to get a new access token from Entra ID (user doesn't see anything)
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>

                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 24 }}>4.</Typography>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          No token or refresh fails → Throw InteractionRequiredAuthError
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Developer must call interactive method (loginPopup/loginRedirect) to prompt user
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                </Stack>
              </Box>

              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                  Common Cache Issues & Solutions:
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                        <TableCell sx={{ fontWeight: 600 }}>Symptom</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Likely Cause</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Solution</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell>Prompted to sign in every page</TableCell>
                        <TableCell>Cache cleared on navigation</TableCell>
                        <TableCell>Check cacheLocation (use sessionStorage or localStorage, not memory)</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>SSO doesn't work across tabs</TableCell>
                        <TableCell>Using sessionStorage</TableCell>
                        <TableCell>Change to localStorage (trade-off: slightly less secure)</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Works in Chrome, fails Safari</TableCell>
                        <TableCell>Using deprecated implicit flow</TableCell>
                        <TableCell>Upgrade to MSAL v2+ with Auth Code Flow + PKCE</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>acquireTokenSilent always fails</TableCell>
                        <TableCell>No account set or wrong scopes</TableCell>
                        <TableCell>Set active account, verify scope strings match portal permissions</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              <Box sx={{ bgcolor: '#e3f2fd', p: 2, borderRadius: 1, border: '1px solid #90caf9' }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.dark', mb: 1 }}>
                  💡 Pro Tips:
                </Typography>
                <Stack spacing={0.5} sx={{ ml: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    • Access tokens typically expire after 1 hour (configurable in Entra ID)
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    • Refresh tokens can last days/weeks depending on tenant policies
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    • MSAL automatically refreshes tokens 5 minutes before expiration
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    • Each unique combination of scopes creates a separate cache entry
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    • Clearing cookies won't clear MSAL cache (use logout or this tool)
                  </Typography>
                </Stack>
              </Box>
            </Stack>
          </AccordionDetails>
        </Accordion>
      </Card>

      {copyOk && (
        <Paper
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            p: 2,
            bgcolor: 'success.main',
            color: 'white',
            borderRadius: 2,
            boxShadow: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <CheckCircleIcon />
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Copied to clipboard!
          </Typography>
        </Paper>
      )}
    </Container>
  );
};

export default MsalCacheInspector;
